import React, { useState } from 'react'
import isEqual from 'lodash/isEqual'
import toNumber from 'lodash/toNumber'
import { Col, Row, Stack } from 'react-bootstrap'
import type { PivotLayoutMeasure, PivotMeasureFormat } from '@ztechapps/common.contract'
import type { EnumSelectProps, SelectOption } from '@ztechapps/webui.input'
import { PivotNegativeNumberFormatEnum, PivotTextAlignEnum } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { Field, FormModal } from '@ztechapps/webui.form'
import { CheckboxInput, EnumSelect, NumberInput, RadioInputGroup, Select, TextInput } from '@ztechapps/webui.input'
import { PIVOT_MAX_DECIMAL_PLACES } from '../pivot/constants'
import { resolvePivotMeasureFormat } from '../pivot/pivotLayout.utils'
import { toPivotMeasureLabel } from '../pivot/toPivotMeasureLabel'
import { usePivotViewerContext } from './Context'

type PivotTextAlignSelectProps = Omit<EnumSelectProps<PivotTextAlignEnum>, 'enumObject'>
const PivotTextAlignSelect = (props: PivotTextAlignSelectProps) => (
  <EnumSelect enumObject={PivotTextAlignEnum} {...props} />
)

const THOUSANDS_SEPARATOR_OPTIONS = [
  { label: 'None', value: '' },
  { label: 'Space', value: ' ' },
  { label: 'Comma', value: ',' },
  { label: 'Period', value: '.' },
]

const DECIMAL_SEPARATOR_OPTIONS = [
  { label: 'Period', value: '.' },
  { label: 'Comma', value: ',' },
]

const NEGATIVE_NUMBER_FORMAT_OPTIONS = [
  { label: '-1', value: PivotNegativeNumberFormatEnum.Minus },
  { label: '(1)', value: PivotNegativeNumberFormatEnum.Parentheses },
]

/** Props for the pivot format cells dialog. */
export type PivotFormatModalProps = {
  measures: PivotLayoutMeasure[]
  shownMeasures: PivotLayoutMeasure[]
  onApply: (measures: PivotLayoutMeasure[]) => void
}

type FormatCellsFieldsProps = {
  choices: SelectOption[]
  initialFormats: Required<PivotMeasureFormat>[]
}

/** Choose-value picker plus the picked measure's format fields — every measure's edits stay staged in the form. */
const FormatCellsFields = ({ choices, initialFormats }: FormatCellsFieldsProps) => {
  const { container } = usePivotViewerContext()
  // the Select holds string values, so each choice carries its measure index as one
  const [measureIndex, setMeasureIndex] = useState<number | undefined>(
    choices[0] ? toNumber(choices[0].value) : undefined
  )
  const stored = initialFormats[measureIndex].thousandsSeparator
  // a stored separator outside the four stock options stays pickable instead of vanishing
  const thousandsSeparatorOptions = THOUSANDS_SEPARATOR_OPTIONS.some((option) => option.value === stored)
    ? THOUSANDS_SEPARATOR_OPTIONS
    : [...THOUSANDS_SEPARATOR_OPTIONS, { label: stored, value: stored }]

  return (
    <Stack gap={3}>
      <Select
        aria-label="Choose value"
        isClearable={false}
        options={choices}
        value={choices.find((choice) => toNumber(choice.value) === measureIndex)}
        onChange={(choice: SelectOption) => setMeasureIndex(toNumber(choice.value))}
        menuPortalTarget={container}
      />
      <Row className="g-3">
        <Col md={6}>
          <Field
            name={`formats.${measureIndex}.textAlign`}
            label="Text align"
            component={PivotTextAlignSelect}
            onChange
            isClearable={false}
            menuPortalTarget={container}
          />
        </Col>
        <Col md={6}>
          <Field
            name={`formats.${measureIndex}.thousandsSeparator`}
            label="Thousands separator"
            component={RadioInputGroup}
            options={thousandsSeparatorOptions}
          />
        </Col>
        <Col md={6}>
          <Field
            name={`formats.${measureIndex}.decimalSeparator`}
            label="Decimal separator"
            component={RadioInputGroup}
            options={DECIMAL_SEPARATOR_OPTIONS}
          />
        </Col>
        <Col md={6}>
          <Field
            name={`formats.${measureIndex}.decimalPlaces`}
            label="Decimal places"
            component={NumberInput}
            min={0}
            max={PIVOT_MAX_DECIMAL_PLACES}
          />
        </Col>
        <Col md={6}>
          <Field name={`formats.${measureIndex}.currencySymbol`} label="Currency symbol" component={TextInput} />
        </Col>
        <Col md={6}>
          <Field
            name={`formats.${measureIndex}.negativeNumberFormat`}
            label="Negative number format"
            component={RadioInputGroup}
            options={NEGATIVE_NUMBER_FORMAT_OPTIONS}
          />
        </Col>
        <Col md={6}>
          <Field name={`formats.${measureIndex}.nullValue`} label="Null value" component={TextInput} />
        </Col>
        <Col md={12}>
          <Field
            name={`formats.${measureIndex}.isPercent`}
            label="Format as percent"
            component={CheckboxInput}
            onChange
          />
        </Col>
      </Row>
    </Stack>
  )
}

/** Toolbar dialog staging every shown measure's display format until applied — staged-Apply shape follows `PivotOptionsModal`. */
export const PivotFormatModal = ({ measures, shownMeasures, onApply }: PivotFormatModalProps) => {
  const { container } = usePivotViewerContext()
  const choices = measures
    .map((measure, index) => ({ measure, index }))
    .filter(({ measure }) => shownMeasures.includes(measure))
    .map(({ measure, index }) => ({ value: `${index}`, label: toPivotMeasureLabel(measure) }))
  const initialFormats = measures.map(resolvePivotMeasureFormat)

  return (
    <FormModal<{ formats: Required<PivotMeasureFormat>[] }>
      trigger={({ onClick }) => <BusyButton icon="pen-to-square" text="Format" onClick={onClick} />}
      title="Format cells"
      saveText="Apply"
      size="lg"
      resizable
      resizeStorageKey="pivot-format"
      container={container}
      initialValues={{ formats: initialFormats }}
      onSubmit={({ formats }) =>
        onApply(
          measures.map((measure, index) =>
            isEqual(formats[index], initialFormats[index])
              ? measure
              : { ...measure, format: { ...formats[index], decimalPlaces: toNumber(formats[index].decimalPlaces) } }
          )
        )
      }
    >
      <FormatCellsFields choices={choices} initialFormats={initialFormats} />
    </FormModal>
  )
}
